import { createContext, useContext } from "react";

import type { DialogHeaderAlign, DialogSize } from "./types";

/**
 * Internal context shared between `Dialog.Root` and its sub-components so
 * sub-components don't need to receive Root-level config via props.
 */
export interface DialogContextValue {
  size: DialogSize;
  dismissible: boolean;
  /** Whether Header/Footer render their separator borders. */
  divider: boolean;
  /** The Body's content overflows it (it scrolls), so the Footer shows its divider. */
  bodyOverflowing: boolean;
  /** Reported by `Dialog.Body`. */
  setBodyOverflowing: (overflowing: boolean) => void;
  /** A `Dialog.Body` is mounted, so the Footer sits below it. */
  hasBody: boolean;
  /** Reported by `Dialog.Body` on mount and unmount. */
  setHasBody: (hasBody: boolean) => void;
  /** Unconditionally closes the dialog — wired onto `Dialog.Close`. */
  requestClose: () => void;
}

export const DialogContext = createContext<DialogContextValue | null>(null);

export const useDialogContext = (): DialogContextValue => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error(
      "Dialog sub-components must be rendered inside <Dialog.Root>. " +
        "Did you forget to wrap them?",
    );
  }
  return ctx;
};

/** The enclosing Header's alignment, which Title and Description follow. */
export const DialogHeaderAlignContext = createContext<DialogHeaderAlign>("left");

/**
 * Per-size desktop width classes (the `dialogWidth` tokens). Mobile (`< md`) is
 * always fluid: the viewport less `spacing-lg` (16) each side.
 *
 * Spelled out as literals so Tailwind JIT picks them up during compile.
 */
export const dialogSizeClasses: Record<DialogSize, string> = {
  sm: "md:max-w-dialog-sm",
  md: "md:max-w-dialog-md",
  lg: "md:max-w-dialog-lg",
};

/**
 * Surface classes shared by every dialog regardless of size — background,
 * radius, shadow, mobile fluid width, max height (body scrolls past this).
 */
export const dialogSurfaceBase =
  "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 " +
  "flex w-[calc(100vw-2*theme(spacing.spacing-lg))] flex-col overflow-hidden " +
  "rounded-radius-lg md:rounded-radius-xl bg-luce-neutral-background-default shadow-modal " +
  "max-h-[85dvh] outline-none";

/**
 * Web animation classes that react to Radix's `data-state` attribute.
 *
 * `slide-in-from-left-1/2` + `slide-in-from-top-[44%]` are load-bearing, not
 * decorative. The surface is centered with `-translate-x/y-1/2`, but the
 * `tailwindcss-animate` enter/exit keyframes overwrite `transform` wholesale —
 * without these the translate vars default to 0 and the dialog animates in
 * from the viewport's bottom-right quadrant. `[44%]` (vs the resting `-50%`)
 * gives a ~6% upward "rise into place" on enter; the exit mirrors it.
 *
 * Native uses Reanimated entering/exiting in the primitive layer instead.
 */
export const dialogSurfaceAnimation =
  "duration-200 " +
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 " +
  "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[44%] " +
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 " +
  "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[44%]";

/**
 * The overlay's full-screen layer behind the surface: it covers the page and
 * fades with the dialog. A custom `overlay` node is drawn inside it.
 */
export const dialogOverlayLayer =
  "fixed inset-0 z-40 " +
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 " +
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0";

/**
 * Default scrim. Matches Figma `overlay/overlay-20` (`#11121433`).
 * Consumers can override via the `overlay` prop on `Dialog.Root`.
 */
export const dialogOverlayClasses = `${dialogOverlayLayer} bg-luce-overlay-20`;

/** Header / Body / Footer padding: 16 on mobile, 24 from `md` (DS-044). */
export const dialogSectionPadding = "p-spacing-lg md:p-spacing-xl";

/**
 * The Footer below a Body that doesn't scroll drops its top padding on mobile,
 * so the Body's 16 bottom padding is the one gap above the buttons (Ardhi,
 * 2026-11-03: 16 on mobile, matching Figma; 48 on desktop, 24 + 24).
 */
export const dialogFooterAfterBody = "pt-0 md:pt-spacing-xl";

/** Header and Footer gap: 8 on mobile, 12 from `md` (Figma's desktop mode). */
export const dialogSectionGap = "gap-spacing-sm md:gap-spacing-md";

/**
 * Separator color for the Header (`border-b`, always) and the Footer
 * (`border-t`, while the body scrolls). Matches the Divider atom's default
 * `mediumEmphasis` token.
 */
export const dialogDividerColor = "border-luce-neutral-border-mediumEmphasis";
